import { FilterType } from 'components/Shared/Table'
import { parsePrice } from 'utils/utils'
import styles from 'components/Order/OrderTable/OrderTable.module.css'
import InputAdornment from '@material-ui/core/InputAdornment'
import TableIconTooltip from 'components/Shared/Table/TableTooltip'
import InfoIcon from '@material-ui/icons/InfoOutlined'
import orderStatusOptions from 'components/Order/OrderTable/orderStatusOptions'
import driverStatusOptions from 'constants/driverStatusOptions'
import { truncateContentAndAddTooltip } from 'components/Shared/Table/TableBodyRow/TableBodyRow'
import { CommentEdit } from 'components/Order/CommentEdit/CommentEdit'
import React from 'react'
import {
  ORDER_ADDITIONAL_INFO_WRITE,
  ORDER_CARRIER_READ,
  ORDER_CONTRACTOR_READ,
  ORDER_COST_READ,
  ORDER_DATA_READ,
  ORDER_DELIVERY_POINT_READ,
  ORDER_DISPATCHER_READ,
  ORDER_FORWARDER_READ,
  ORDER_INCOME_READ
} from 'context/auth'

const priceColumnDefinitions = [
  { path: 'pricePLN.customerPrice', filterName: 'customerPricePLN', requiredPrivileges: [ORDER_INCOME_READ] },
  { path: 'pricePLN.carrierPrice', filterName: 'carrierPricePLN', requiredPrivileges: [ORDER_COST_READ] },
  { path: 'pricePLN.profit', filterName: 'profitPLN', requiredPrivileges: [ORDER_INCOME_READ, ORDER_COST_READ] },
  { path: 'priceEUR.customerPrice', filterName: 'customerPriceEUR', requiredPrivileges: [ORDER_INCOME_READ], visibleByDefault:true },
  { path: 'priceEUR.carrierPrice', filterName: 'carrierPriceEUR', requiredPrivileges: [ORDER_COST_READ] , visibleByDefault:true},
  { path: 'priceEUR.profit', filterName: 'profitEUR', requiredPrivileges: [ORDER_INCOME_READ, ORDER_COST_READ], visibleByDefault:true },
  { path: 'priceUSD.customerPrice', filterName: 'customerPriceUSD', requiredPrivileges: [ORDER_INCOME_READ] },
  { path: 'priceUSD.carrierPrice', filterName: 'carrierPriceUSD', requiredPrivileges: [ORDER_COST_READ] },
  { path: 'priceUSD.profit', filterName: 'profitUSD', requiredPrivileges: [ORDER_INCOME_READ, ORDER_COST_READ] },
  { path: 'priceGBP.customerPrice', filterName: 'customerPriceGBP', requiredPrivileges: [ORDER_INCOME_READ] },
  { path: 'priceGBP.carrierPrice', filterName: 'carrierPriceGBP', requiredPrivileges: [ORDER_COST_READ] },
  { path: 'priceGBP.profit', filterName: 'profitGBP', requiredPrivileges: [ORDER_INCOME_READ, ORDER_COST_READ] },
]

const priceColumn = (definition) => ({
  id: definition.filterName,
  label: `order.${definition.filterName}`,
  path: `${definition.path}`,
  isHiddenByDefault: !definition.visibleByDefault,
  filterType: FilterType.NumberRange,
  filterNames: [`${definition.filterName}Ge`, `${definition.filterName}Le`],
  filterPlaceholders: ['orderTable.minValueFilterPlaceholder', 'orderTable.maxValueFilterPlaceholder'],
  renderFunc: parsePrice
})

export function createColumnDefinitions (hasAnyPrivileges, t, forwarders, dispatchers, tableRef) {

  const orderDataColumns1 = hasAnyPrivileges([ORDER_DATA_READ]) ? [
    {
      id: 'readableId',
      label: 'order.id',
      path: 'readableId',
      filterType: FilterType.Text,
      filterInputProps: {
        className: styles.orderIdFilterInput,
        inputProps: {
          maxLength: 9
        },
        endAdornment:
          <InputAdornment position="end">
            <TableIconTooltip title={t('order.idFilterCaption')} arrow>
              <InfoIcon className={styles.orderIdFilterInfoIcon}/>
            </TableIconTooltip>
          </InputAdornment>
      }
    },
    {
      id: 'status',
      label: 'order.status.title',
      path: 'status',
      sortable: true,
      filterType: FilterType.Select,
      selectOptions: orderStatusOptions,
      statusToIndicatorColorMap: {
        SAVED: '#398BFF',
        SENT_TO_DRIVER: '#FFD7F0',
        READ_BY_DRIVER: '#D6C99E',
        ACCEPTED_BY_DRIVER: '#5EFF38',
        REJECTED: '#FF0003',
        ACCEPTED_FOR_FULFILLMENT: '#0FBFF9',
        LOADING: '#CABBFF',
        LOADED_NOT_APPROVED: '#7967BB',
        LOADED: '#B200FF',
        UNLOADING: '#00C2D5',
        UNLOADED_NOT_APPROVED: '#0091A1',
        UNLOADED: '#8100C7',
        FINISHED: '#0E6A00',
        CANCELLED: '#000'
      }
    }
  ] : []
  const orderDataColumns2 = hasAnyPrivileges([ORDER_DATA_READ]) ? [
    {
      id: 'referenceId',
      label: 'order.referenceId',
      path: 'referenceId',
      filterType: FilterType.Text
    }
  ] : []
  const orderDataColumns3 = hasAnyPrivileges([ORDER_DATA_READ]) ? [
    {
      id: 'creationDate',
      label: 'order.issueDate',
      path: 'creationDate',
      sortable: true,
      isHiddenByDefault: true,
      filterType: FilterType.DateRange,
      filterNames: ['creationDateGe', 'creationDateLe']
    },
    {
      id: 'lastEditedDateTime',
      label: 'order.lastEditedDate',
      path: 'lastEditedDateTime',
      sortable: true,
      isHiddenByDefault: true,
      filterType: FilterType.DateRange,
      filterNames: ['lastEditedDateGe', 'lastEditedDateLe']
    },
  ] : []
  const orderAdditionalDataColumns = hasAnyPrivileges([ORDER_ADDITIONAL_INFO_WRITE]) ? [
    {
      id: 'orderComment',
      label: 'order.comment',
      path: 'comment',
      filterType: FilterType.None,
      renderFunc: (cell, row, col, isHovered) =>
        (
          <>
            {cell ? truncateContentAndAddTooltip(cell, 25) : ''}
            <CommentEdit row={row}
                         visible={isHovered}
                         onSuccess={() => tableRef.current.refreshContent()}
            />
          </>
        )
    },
  ] : []
  const orderClientColumns = hasAnyPrivileges([ORDER_CONTRACTOR_READ]) ? [
    {
      id: 'clientIds',
      label: 'order.client',
      path: 'clientId',
      filterType: FilterType.Contractor,
      selectOptions: []
    }
  ] : []
  const orderPayerColumns = hasAnyPrivileges([ORDER_CONTRACTOR_READ]) ? [
    {
      id: 'payerIds',
      label: 'order.payer',
      path: 'payerId',
      isHiddenByDefault: true,
      filterType: FilterType.Contractor,
      selectOptions: []
    }
  ] : []
  const orderCarrierColumns1 = hasAnyPrivileges([ORDER_CARRIER_READ]) ? [
    {
      id: 'carrierIds',
      label: 'order.carrier',
      path: 'carrierId',
      filterType: FilterType.Contractor,
      selectOptions: []
    },
    {
      id: 'carRegistrationNumber',
      label: 'order.carRegistrationNumber',
      path: 'carRegistrationNumber',
      filterType: FilterType.Text
    }
  ] : []
  const orderCarrierColumns2 = hasAnyPrivileges([ORDER_CARRIER_READ]) ? [
    {
      id: 'driverStatuses',
      label: 'driver.status.title',
      path: 'driverStatus',
      isHiddenByDefault: true,
      filterType: FilterType.Select,
      selectOptions: driverStatusOptions,
      statusToIndicatorColorMap: {
        TRAFFIC_JAM: '#FFEE18',
        ACCIDENT: '#ED1C24',
        AVAILABLE: '#65D609',
        UNAVAILABLE: '#030B6F'
      }
    },
    {
      id: 'distanceToNextDeliveryPointKm',
      label: 'order.distanceToNextDeliveryPoint',
      path: 'distanceToNextDeliveryPointKm',
      sortable: true,
      renderFunc: distance => distance === null ? '―' : distance,
      isHiddenByDefault: true,
      filterType: FilterType.NumberRange,
      filterNames: ['distanceToNextDeliveryPointKmGe', 'distanceToNextDeliveryPointKmLe'],
      filterPlaceholders: ['orderTable.minValueFilterPlaceholder', 'orderTable.maxValueFilterPlaceholder']
    }
  ] : []
  const orderDispatcherColumns = hasAnyPrivileges([ORDER_DISPATCHER_READ]) ? [
    {
      id: 'dispatcherIds',
      label: 'order.dispatcherId',
      path: 'dispatcherId',
      isHiddenByDefault: true,
      filterType: FilterType.Select,
      selectOptions: dispatchers
    }
  ] : []
  const orderForwarderColumns = hasAnyPrivileges([ORDER_FORWARDER_READ]) ? [
    {
      id: 'forwarderIds',
      label: 'order.forwarderId',
      path: 'forwarderId',
      filterType: FilterType.Select,
      selectOptions: forwarders
    }
  ] : []

  const orderDeliveryPointsColumns1 = hasAnyPrivileges([ORDER_DELIVERY_POINT_READ]) ? [
    {
      id: 'firstLoadingLocation',
      label: 'order.delivery.loading.from',
      path: 'firstLoadingLocation',
      filterType: FilterType.Text
    },
    {
      id: 'lastUnloadingLocation',
      label: 'order.delivery.unloading.to',
      path: 'lastUnloadingLocation',
      filterType: FilterType.Text
    },
    {
      id: 'firstLoadingDate',
      label: 'order.delivery.loading.title',
      path: 'firstLoadingDate',
      sortable: true,
      filterType: FilterType.Date,
      dateDiffIndicator: true
    },
    {
      id: 'lastUnloadingDate',
      label: 'order.delivery.unloading.title',
      path: 'lastUnloadingDate',
      sortable: true,
      filterType: FilterType.Date,
      dateDiffIndicator: true
    },
  ] : []
  const orderDeliveryPointsColumns2 = hasAnyPrivileges([ORDER_DELIVERY_POINT_READ]) ? [
    {
      id: 'firstLoadingCity',
      label: 'order.delivery.loading.fromCity',
      path: 'firstLoadingCity',
      sortable: true,
      isHiddenByDefault: true,
      filterType: FilterType.Text
    },
    {
      id: 'lastUnloadingCity',
      label: 'order.delivery.unloading.toCity',
      path: 'lastUnloadingCity',
      sortable: true,
      isHiddenByDefault: true,
      filterType: FilterType.Text
    }
  ] : []
  const orderPriceColumns = priceColumnDefinitions
    .filter(definition => definition.requiredPrivileges.every(privilege => hasAnyPrivileges([privilege])))
    .map(priceColumn)

  return [
    ...orderDataColumns1,
    ...orderClientColumns,
    ...orderDataColumns2,
    ...orderCarrierColumns1,
    ...orderDeliveryPointsColumns1,
    ...orderForwarderColumns,
    ...orderAdditionalDataColumns,

    ...orderDeliveryPointsColumns2,
    ...orderCarrierColumns2,
    ...orderDataColumns3,
    ...orderPayerColumns,
    ...orderDispatcherColumns,
    ...orderPriceColumns
  ]
}
